// ── Remote session copy (TPT495) ──
//
// English and Ukrainian strings for the task view's device picker and remote terminal. The web SPA
// has no general i18n service, so this follows the per-module table pattern of settings.js's
// NOTIFICATION_TEXT, keyed by the project's `language`. Ukrainian follows the glossary in
// tt-i18n.md. Values are plain text: render them through escapeHtml() or textContent.

const TEXT = {
  en: {
    sectionTitle: 'Run on device',
    device: 'Device',
    agent: 'Agent',
    taskDefaultAgent: 'Task default',
    start: 'Start',
    starting: 'Starting…',
    attach: 'Attach',
    stop: 'Stop',
    stopping: 'Stopping…',
    stopPrompt: 'Stop {task} on {device}? The agent ends right away.',
    stopKeep: 'Keep running',
    stopConfirm: 'Stop session',
    checkAgain: 'Check again',
    checking: 'Checking your devices…',
    tryAgain: 'Try again',
    loadFailed: 'Could not check your devices.',
    noDevices: 'None of your devices is online for this project.',
    noDevicesHint: 'Keep Task App open on the device and set REMOTE_CONTROL_ENABLED to true in this project’s .tipatask/config.json there.',
    assignedToOther: 'This task is assigned to another member, so it cannot be started here.',
    viewOnly: 'Your role can watch sessions but cannot start or stop them.',
    noStartDevice: 'No online device can start this task.',
    runningElsewhere: 'Already running on {device}, which cannot be reached from here.',
    connectingDevices: 'Connecting to your devices…',
    reconnectingDevices: 'Connection to your devices was lost. Reconnecting…',
    signInAgain: 'Your sign-in expired. Sign in again to reach your devices.',
    relayStopped: 'The connection to your devices was closed.',
    accessLost: 'You no longer have access to sessions in this project.',
    stateStarting: 'Starting',
    stateRunning: 'Running',
    stateOffline: 'Device offline',
    terminalTitle: '{task} on {device}',
    terminalLabel: 'Terminal of {task}',
    connecting: 'Connecting…',
    reconnecting: 'Reconnecting…',
    connected: 'Connected',
    disconnected: 'Disconnected',
    sessionEnded: 'Session ended',
    roleControl: 'You control input',
    roleReadOnly: 'Read-only: your role cannot type here',
    roleLocal: 'Read-only: Task App on this device has control',
    roleOther: 'Read-only: another browser window has control',
    roleNone: 'Read-only: nobody has control yet',
    noticeReconnecting: 'Connection lost. Reconnecting and restoring recent output…',
    noticeDeviceOffline: 'The device went offline. The terminal comes back if it returns soon.',
    noticeDeviceLost: 'The device did not come back, so this session can no longer be reached.',
    noticeExited: 'The session ended.',
    noticeExitedCode: 'The session ended with exit code {code}.',
    noticePermissionLost: 'You no longer have access to this session.',
    noticeSignIn: 'Your sign-in expired. Sign in again to reconnect.',
    noticeRelayStopped: 'The connection was closed and will not retry by itself.',
    noticeGap: 'Some output was dropped. Restoring recent output…',
    noticeLoadFailed: 'Could not load the terminal. Reload the page and try again.',
    hintControl: 'Keys go to the agent. Press Esc twice to close; the session keeps running.',
    hintReadOnly: 'Press Esc to close. The session keeps running.',
    codeFallback: 'The device did not complete that action.',
    code_device_not_eligible: 'This device cannot start tasks in this project.',
    code_device_offline: 'The device is offline.',
    code_capability_missing: 'This device does not support that action.',
    code_agent_unavailable: 'That agent is not available on this device.',
    code_already_running: 'This task is already running on that device.',
    code_start_in_flight: 'This task is already starting on that device.',
    code_session_not_found: 'That session is no longer running.',
    code_forbidden: 'Your role cannot do that.',
    code_too_many_attachments: 'Too many terminals are open. Close one and try again.',
    code_device_busy: 'The device is busy. Try again in a moment.',
    code_command_id_reused: 'That action was refused. Try again.',
    code_relay_unavailable: 'The relay is unavailable. Try again in a moment.',
    code_busy: 'The relay is busy. Try again in a moment.',
    code_device_timeout: 'The device did not answer in time.',
    code_device_disconnected: 'The device disconnected before it answered.',
    code_project_not_allowed: 'This device no longer serves this project.',
    code_remote_control_disabled: 'Remote control was turned off on the device.',
    code_task_not_startable: 'This task cannot run in a terminal.',
    code_task_not_found: 'The device could not find this task.',
    code_task_assigned_to_other_member: 'This task is assigned to another member.',
    code_task_claim_failed: 'Another device has claimed this task.',
    code_authentication_required: 'Task App on this device needs you to sign in again.',
    code_start_failed: 'The agent did not start on the device.',
    code_read_only: 'This terminal is read-only for you.',
    code_rate_limited: 'Too much input at once. Slow down and try again.',
    code_input_too_large: 'That paste is too large to send.',
  },
  uk: {
    sectionTitle: 'Запуск на пристрої',
    device: 'Пристрій',
    agent: 'Агент',
    taskDefaultAgent: 'Типовий для завдання',
    start: 'Запустити',
    starting: 'Запуск…',
    attach: 'Під’єднатися',
    stop: 'Зупинити',
    stopping: 'Зупинка…',
    stopPrompt: 'Зупинити {task} на {device}? Агент завершить роботу негайно.',
    stopKeep: 'Не зупиняти',
    stopConfirm: 'Зупинити сесію',
    checkAgain: 'Перевірити ще раз',
    checking: 'Перевіряємо ваші пристрої…',
    tryAgain: 'Спробувати ще раз',
    loadFailed: 'Не вдалося перевірити ваші пристрої.',
    noDevices: 'Жоден із ваших пристроїв не в мережі для цього проєкту.',
    noDevicesHint: 'Тримайте Task App відкритим на пристрої та задайте REMOTE_CONTROL_ENABLED значення true у файлі .tipatask/config.json цього проєкту.',
    assignedToOther: 'Це завдання призначено іншому учаснику, тому його не можна запустити тут.',
    viewOnly: 'Ваша роль дозволяє переглядати сесії, але не запускати чи зупиняти їх.',
    noStartDevice: 'Жоден пристрій у мережі не може запустити це завдання.',
    runningElsewhere: 'Уже виконується на {device}, а цей пристрій недоступний звідси.',
    connectingDevices: 'Під’єднуємося до ваших пристроїв…',
    reconnectingDevices: 'З’єднання з вашими пристроями втрачено. Під’єднуємося знову…',
    signInAgain: 'Термін дії входу завершився. Увійдіть знову, щоб отримати доступ до пристроїв.',
    relayStopped: 'З’єднання з вашими пристроями закрито.',
    accessLost: 'Ви більше не маєте доступу до сесій цього проєкту.',
    stateStarting: 'Запускається',
    stateRunning: 'Виконується',
    stateOffline: 'Пристрій не в мережі',
    terminalTitle: '{task} на {device}',
    terminalLabel: 'Термінал завдання {task}',
    connecting: 'Під’єднання…',
    reconnecting: 'Повторне під’єднання…',
    connected: 'Під’єднано',
    disconnected: 'Від’єднано',
    sessionEnded: 'Сесію завершено',
    roleControl: 'Ввід у ваших руках',
    roleReadOnly: 'Лише перегляд: ваша роль не дозволяє вводити текст',
    roleLocal: 'Лише перегляд: ввід контролює Task App на цьому пристрої',
    roleOther: 'Лише перегляд: ввід контролює інше вікно браузера',
    roleNone: 'Лише перегляд: ввід ще ніхто не контролює',
    noticeReconnecting: 'З’єднання втрачено. Під’єднуємося знову й відновлюємо останній вивід…',
    noticeDeviceOffline: 'Пристрій вийшов із мережі. Термінал відновиться, якщо пристрій незабаром повернеться.',
    noticeDeviceLost: 'Пристрій не повернувся, тому ця сесія більше недоступна.',
    noticeExited: 'Сесію завершено.',
    noticeExitedCode: 'Сесію завершено з кодом виходу {code}.',
    noticePermissionLost: 'Ви більше не маєте доступу до цієї сесії.',
    noticeSignIn: 'Термін дії входу завершився. Увійдіть знову, щоб під’єднатися.',
    noticeRelayStopped: 'З’єднання закрито, і автоматичних повторних спроб не буде.',
    noticeGap: 'Частину виводу втрачено. Відновлюємо останній вивід…',
    noticeLoadFailed: 'Не вдалося завантажити термінал. Оновіть сторінку й спробуйте ще раз.',
    hintControl: 'Клавіші надходять до агента. Натисніть Esc двічі, щоб закрити; сесія продовжить працювати.',
    hintReadOnly: 'Натисніть Esc, щоб закрити. Сесія продовжить працювати.',
    codeFallback: 'Пристрій не виконав цю дію.',
    code_device_not_eligible: 'Цей пристрій не може запускати завдання в цьому проєкті.',
    code_device_offline: 'Пристрій не в мережі.',
    code_capability_missing: 'Цей пристрій не підтримує цю дію.',
    code_agent_unavailable: 'Цей агент недоступний на пристрої.',
    code_already_running: 'Це завдання вже виконується на цьому пристрої.',
    code_start_in_flight: 'Це завдання вже запускається на цьому пристрої.',
    code_session_not_found: 'Ця сесія більше не виконується.',
    code_forbidden: 'Ваша роль цього не дозволяє.',
    code_too_many_attachments: 'Відкрито забагато терміналів. Закрийте один і спробуйте ще раз.',
    code_device_busy: 'Пристрій зайнятий. Спробуйте трохи згодом.',
    code_command_id_reused: 'Цю дію відхилено. Спробуйте ще раз.',
    code_relay_unavailable: 'Ретранслятор недоступний. Спробуйте трохи згодом.',
    code_busy: 'Ретранслятор зайнятий. Спробуйте трохи згодом.',
    code_device_timeout: 'Пристрій не відповів вчасно.',
    code_device_disconnected: 'Пристрій від’єднався, не відповівши.',
    code_project_not_allowed: 'Цей пристрій більше не обслуговує цей проєкт.',
    code_remote_control_disabled: 'Віддалене керування на пристрої вимкнено.',
    code_task_not_startable: 'Це завдання не можна виконати в терміналі.',
    code_task_not_found: 'Пристрій не знайшов це завдання.',
    code_task_assigned_to_other_member: 'Це завдання призначено іншому учаснику.',
    code_task_claim_failed: 'Це завдання вже закріплено за іншим пристроєм.',
    code_authentication_required: 'Task App на цьому пристрої потребує повторного входу.',
    code_start_failed: 'Агент не запустився на пристрої.',
    code_read_only: 'Цей термінал доступний вам лише для перегляду.',
    code_rate_limited: 'Забагато вводу за раз. Зачекайте й спробуйте ще раз.',
    code_input_too_large: 'Цей вставлений текст завеликий для надсилання.',
  },
};

export function remoteText(language, key, params = {}) {
  const table = TEXT[language] ?? TEXT.en;
  const template = table[key] ?? TEXT.en[key] ?? key;
  return template.replace(/\{(\w+)\}/g, (match, name) => (name in params ? String(params[name]) : match));
}

// Text for a relay or device refusal code. A code this build has not heard of gets the generic
// sentence, never the raw identifier.
export function remoteCodeText(language, code) {
  const key = `code_${code}`;
  return remoteText(language, key in TEXT.en ? key : 'codeFallback');
}

// Test seam: the tables, so a test can hold the two languages to the same keys.
export const REMOTE_TEXT = TEXT;
